import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Banner, Button, Card, ConfirmDialog, Field, Switch, Tag,
} from '../kit';
import {
  apiErrorMessage, deleteOrganization, fetchOrgConfigForOrg, getCurrencies, patchOrganization,
} from '../../../utils/organizationApi';
import { getJournalEntryTypes, updateOrgConfig } from '../../../utils/api';
import { SUPPORTED_CURRENCIES } from '../../../utils/currency';
import {
  currencyOptions, generalFromOrg, generalPatch, MFA_METHODS, mfaHelp, policiesFromConfig, policiesPatch,
  titleCase, validateGeneral, validatePolicies,
} from './orgModel';
import { CurrencySelect } from './orgsUi';
import CurrencyCard from './CurrencyCard';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function OrgSettingsTab({ org, setActions, onDirtyChange, onSaved, onDeleted }) {
  const ids = { name: useId(), code: useId(), currency: useId(), close: useId(), cfg: useId() };

  const [baseGeneral, setBaseGeneral] = useState(() => generalFromOrg(org));
  const [general, setGeneral] = useState(() => generalFromOrg(org));
  const [configState, setConfigState] = useState({ loading: true, error: null, doc: null });
  const [basePolicies, setBasePolicies] = useState(() => policiesFromConfig(null));
  const [policies, setPolicies] = useState(() => policiesFromConfig(null));
  const [loadedCategories, setLoadedCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmCode, setConfirmCode] = useState(false);

  const loadConfig = useCallback(async () => {
    setConfigState({ loading: true, error: null, doc: null });
    try {
      const doc = await fetchOrgConfigForOrg(org.id);
      const p = policiesFromConfig(doc);
      setBasePolicies(p);
      setPolicies(p);
      setConfigState({ loading: false, error: null, doc });
    } catch (err) {
      setConfigState({ loading: false, error: apiErrorMessage(err, 'Could not load the org config.'), doc: null });
    }
  }, [org.id]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => {
    getCurrencies().then(setCurrencies).catch(() => setCurrencies([]));
    getJournalEntryTypes(org.id)
      .then((types) => {
        const cats = [...new Set((types || [])
          .map((t) => (t?.category ? String(t.category).toLowerCase() : null))
          .filter(Boolean))].sort();
        setLoadedCategories(cats);
      })
      .catch(() => setLoadedCategories([]));
  }, [org.id]);

  const gPatch = useMemo(() => generalPatch(baseGeneral, general), [baseGeneral, general]);
  const pPatch = useMemo(() => policiesPatch(basePolicies, policies), [basePolicies, policies]);
  const dirtyCount = Object.keys(gPatch).length + Object.keys(pPatch).length;
  const policiesEditable = !configState.loading && !configState.error && Boolean(configState.doc);

  useEffect(() => {
    onDirtyChange(dirtyCount > 0);
  }, [dirtyCount, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    if (!dirtyCount) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyCount]);

  const setG = (key, value) => {
    setGeneral((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };
  const setP = (key, value) => {
    setPolicies((prev) => ({ ...prev, [key]: value }));
    if (key === 'mfaMethods' || key === 'mfaEnforced') setErrors((prev) => ({ ...prev, mfaMethods: undefined }));
  };

  const save = async ({ codeConfirmed = false } = {}) => {
    const next = { ...validateGeneral(general), ...validatePolicies(policies) };
    setErrors(next);
    if (Object.keys(next).length) {
      toast.error('Fix the highlighted fields first.');
      return;
    }
    if (gPatch.code && !codeConfirmed) { setConfirmCode(true); return; }
    setSaving(true);
    const failures = [];
    let savedAny = false;
    if (Object.keys(gPatch).length) {
      try {
        const updated = await patchOrganization(org.id, gPatch);
        const fresh = generalFromOrg({ ...org, ...gPatch, ...(updated || {}) });
        setBaseGeneral(fresh);
        setGeneral(fresh);
        savedAny = true;
      } catch (err) {
        const message = apiErrorMessage(err, 'Could not save the general settings.');
        if (err?.response?.status === 409) setErrors((prev) => ({ ...prev, code: message }));
        failures.push(`General: ${message}`);
      }
    }
    if (Object.keys(pPatch).length && policiesEditable) {
      try {
        await updateOrgConfig({ dbOrgId: Number(org.id), ...pPatch });
        setBasePolicies(policies);
        savedAny = true;
      } catch (err) {
        failures.push(`Policies: ${apiErrorMessage(err, 'Could not save the policies.')}`);
      }
    }
    setSaving(false);
    if (failures.length) toast.error(failures.join(' · '), { duration: 8000 });
    else toast.success('Settings saved');
    if (savedAny) onSaved();
  };

  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    setActions(
      <>
        <Button variant="danger" onClick={() => setConfirmDelete(true)}>Delete organization</Button>
        <Button variant="primary" onClick={() => saveRef.current()} disabled={saving || dirtyCount === 0}>
          {saving ? 'Saving…' : dirtyCount ? `Save changes (${dirtyCount})` : 'Save changes'}
        </Button>
      </>
    );
  }, [dirtyCount, saving, setActions]);
  useEffect(() => () => setActions(null), [setActions]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteOrganization(org.id);
      toast.success(`${org.name} deleted`);
      onDirtyChange(false);
      onDeleted();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not delete the organization.'));
      setDeleting(false);
    }
  };

  const options = useMemo(
    () => currencyOptions(currencies, [...SUPPORTED_CURRENCIES, general.reportingCurrency]),
    [currencies, general.reportingCurrency]
  );
  const allCategories = [...new Set([...loadedCategories, ...policies.journalCategories])].sort();
  const addableCategories = loadedCategories.filter((c) => !policies.journalCategories.includes(c));
  const toggleMethod = (key) => setP('mfaMethods', policies.mfaMethods.includes(key)
    ? policies.mfaMethods.filter((m) => m !== key)
    : [...policies.mfaMethods, key]);

  const locationCount = org.locationCount ?? 0;
  const userCount = org.userCount;

  return (
    <div className="ak-cols">
      <div className="orgs-stack">
        <Card title="General">
          <div className="ak-grid ak-2">
            <Field label="Name" required error={errors.name} htmlFor={ids.name}>
              <input id={ids.name} className="ak-input" value={general.name} aria-invalid={Boolean(errors.name)} onChange={(e) => setG('name', e.target.value)} />
            </Field>
            <Field
              label="Code"
              required
              error={errors.code}
              help={gPatch.code ? <span className="orgs-warn-text">User access and the org config are keyed by the code — see the warning when you save.</span> : null}
              htmlFor={ids.code}
            >
              <input id={ids.code} className="ak-input" value={general.code} aria-invalid={Boolean(errors.code)} onChange={(e) => setG('code', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Portfolio currency" error={errors.reportingCurrency} htmlFor={ids.currency}>
              <CurrencySelect id={ids.currency} value={general.reportingCurrency} options={options} onChange={(v) => setG('reportingCurrency', v)} />
            </Field>
            <Field label="Financial close day" error={errors.financialCloseDay} help="Reports treat a month as closed after this day." htmlFor={ids.close}>
              <input id={ids.close} className="ak-input" type="number" min="1" max="31" value={general.financialCloseDay} aria-invalid={Boolean(errors.financialCloseDay)} onChange={(e) => setG('financialCloseDay', e.target.value)} />
            </Field>
          </div>
          <div className="ak-field">
            <span className="ak-label" id={ids.cfg}>Org config ID</span>
            <div className="orgs-readonly" aria-labelledby={ids.cfg}>{org.cosmosOrgConfigId || configState.doc?.id || 'Not linked'}</div>
          </div>
        </Card>

        <Card title="Policies">
          {configState.error ? (
            <Banner tone="bad" icon={AlertTriangle} title="Couldn't load policies" action={<Button size="small" onClick={loadConfig}>Try again</Button>}>
              {configState.error}
            </Banner>
          ) : null}
          {!configState.loading && !configState.error && !configState.doc ? (
            <Banner tone="warn" icon={AlertTriangle} title="No org config for this organization">
              MFA and journal policies live in the org config document, and this organization has none, so they can't be saved here.
            </Banner>
          ) : null}

          <div className="orgs-policy">
            <Switch
              checked={policies.mfaEnforced}
              onChange={(v) => setP('mfaEnforced', v)}
              disabled={!policiesEditable}
              title="Require MFA to sign in"
            />
            <div className="orgs-policy-body">
              <span className="orgs-policy-title">Require MFA to sign in</span>
              <span className="ak-help">
                {configState.loading ? 'Loading…' : policies.mfaEnforced ? mfaHelp(policies.mfaMethods) : 'Off: users sign in with a password only.'}
              </span>
              {policies.mfaEnforced ? (
                <div className="orgs-checks" role="group" aria-label="Allowed sign-in methods">
                  {MFA_METHODS.map((m) => (
                    <label key={m.key} className="orgs-check">
                      <input
                        type="checkbox"
                        className="ak-check"
                        checked={policies.mfaMethods.includes(m.key)}
                        disabled={!policiesEditable}
                        onChange={() => toggleMethod(m.key)}
                      />
                      <span>{m.label}<small>{m.help}</small></span>
                    </label>
                  ))}
                </div>
              ) : null}
              {errors.mfaMethods ? <span className="ak-field-error" role="alert">{errors.mfaMethods}</span> : null}
            </div>
          </div>

          <div className="orgs-policy">
            <Switch
              checked={policies.journalRestricted}
              onChange={(v) => setP('journalRestricted', v)}
              disabled={!policiesEditable}
              title="Limit journal adjustments by category"
            />
            <div className="orgs-policy-body">
              <span className="orgs-policy-title">Limit journal adjustments by category</span>
              {policies.journalRestricted ? (
                <>
                  <div className="ak-tags">
                    {policies.journalCategories.map((c) => (
                      <Tag
                        key={c}
                        removeLabel={`Stop allowing ${c} adjustments`}
                        onRemove={policiesEditable ? () => setP('journalCategories', policies.journalCategories.filter((x) => x !== c)) : undefined}
                      >
                        {titleCase(c)}{loadedCategories.length && !loadedCategories.includes(c) ? ' (not loaded)' : ''}
                      </Tag>
                    ))}
                    {addableCategories.length && policiesEditable ? (
                      <select
                        className="orgs-add-select"
                        value=""
                        aria-label="Allow another category"
                        onChange={(e) => { if (e.target.value) setP('journalCategories', [...policies.journalCategories, e.target.value]); }}
                      >
                        <option value="">+ Add</option>
                        {addableCategories.map((c) => <option key={c} value={c}>{titleCase(c)}</option>)}
                      </select>
                    ) : null}
                  </div>
                  {policies.journalCategories.length === 0 ? (
                    <span className="orgs-warn-text">No categories allowed, so journal adjustments are switched off for this organization.</span>
                  ) : (
                    <span className="ak-help">Adjustments are allowed only for these categories.</span>
                  )}
                  {!allCategories.length ? <span className="ak-help">No journal categories are loaded for this organization yet.</span> : null}
                </>
              ) : (
                <span className="ak-help">Off: adjustments are allowed for every journal category.</span>
              )}
              <Link to="/journal-types" className="orgs-help-link">Categories come from Configuration → Journal types →</Link>
            </div>
          </div>
        </Card>
      </div>

      <CurrencyCard org={org} portfolioCurrency={baseGeneral.reportingCurrency} currencies={currencies} />

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${org.name}?`}
        confirmLabel="Delete organization"
        danger
        busy={deleting}
        confirmText={org.code || org.name}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={doDelete}
      >
        <p style={{ margin: 0 }}>This is a soft delete: nothing is erased, but the organization disappears from Lodgeic for everyone.</p>
        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <li>Its {plural(locationCount, 'location')} and their data stay in the database but no longer appear in dashboards, reports or pickers.</li>
          <li>
            {userCount === null || userCount === undefined ? 'Its users lose' : `Its ${plural(userCount, 'user')} lose`} access to this organization.
            Their accounts, and their access to other organizations, don't change.
          </li>
          <li>Scheduled data refreshes for it stop.</li>
        </ul>
        <p style={{ margin: 0 }}>There's no restore button here. Bringing it back takes a database change.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmCode}
        title="Change the organization code?"
        confirmLabel={`Change to ${String(gPatch.code || '')}`}
        danger
        onCancel={() => setConfirmCode(false)}
        onConfirm={() => { setConfirmCode(false); save({ codeConfirmed: true }); }}
      >
        <p style={{ margin: 0 }}>
          Users' access and the org config are stored under the current code <span className="ak-mono">{baseGeneral.code}</span>.
          Changing it to <span className="ak-mono">{gPatch.code}</span> doesn't move them, so users would lose access to this organization until their access is re-granted under the new code.
        </p>
      </ConfirmDialog>
    </div>
  );
}
